import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { START_SPEC_INTELLIGENCE_RUN } from 'api/graphQL/mutations/specIntelligence.mutations'
import {
    GET_LATEST_SPEC_INTELLIGENCE_REPORT,
    GET_SPEC_INTELLIGENCE_REPORT,
} from 'api/graphQL/queries/specIntelligence.queries'
import type {
    GetLatestSpecIntelligenceReportInput,
    GetLatestSpecIntelligenceReportResponse,
    GetSpecIntelligenceReportInput,
    GetSpecIntelligenceReportResponse,
    SpecIntelligenceReport,
    SpecIntelligenceRunStatus,
    StartSpecIntelligenceRunInput,
    StartSpecIntelligenceRunResponse,
} from 'api/graphQL/types/specIntelligence.types'
import { useCallback, useEffect, useState } from 'react'
import useSpecIntelligencePolling from './useSpecIntelligencePolling'

export type SpecIntelligenceDrawerView = 'loading' | 'picker' | 'progress' | 'results' | 'error'

/** Returned by {@link useSpecIntelligence}. */
export interface UseSpecIntelligenceResult {
    /** Current view for the drawer UI. */
    drawerView: SpecIntelligenceDrawerView
    /** True while a run is building — drives the icon animation. */
    isBuilding: boolean
    /** Latest polling status; null when idle. */
    runStatus: SpecIntelligenceRunStatus | null
    /** Completed report for the results view; null when unavailable. */
    currentReport: SpecIntelligenceReport | null
    /** Section numbers from the report that triggered re-run — pre-populates the picker. */
    rerunSectionNumbers: string[] | null
    /** Trigger a new analysis run with the given section IDs. */
    startRun: (selectedLeafIds: string[]) => Promise<void>
    /** Reset back to the picker view after viewing results. */
    handleRerun: () => void
}

/**
 * Owns all SpecIntelligence API interactions: initial report check, run-status polling,
 * final report fetch, and the start-run mutation. Components receive data and callbacks
 * via the returned object — no GraphQL in the UI layer.
 *
 * @param projectId - The project whose SpecIntelligence data to load.
 * @param skip - When true, all queries and mutations are suppressed (e.g. drawer not visible).
 */
const useSpecIntelligence = (projectId: string, skip = false): UseSpecIntelligenceResult => {
    const [drawerView, setDrawerView] = useState<SpecIntelligenceDrawerView>('loading')
    const [currentCompleteReport, setCurrentCompleteReport] = useState<SpecIntelligenceReport | null>(null)
    const [rerunSectionNumbers, setRerunSectionNumbers] = useState<string[] | null>(null)

    // Polling (survives drawer close / tab switches)
    const { isBuilding, runStatus, startPolling } = useSpecIntelligencePolling(skip || !projectId)

    // Initial report query on render to check for existing data
    const { loading: initialReportLoading, data: initialReportData } = useQuery<
        GetLatestSpecIntelligenceReportResponse,
        GetLatestSpecIntelligenceReportInput
    >(GET_LATEST_SPEC_INTELLIGENCE_REPORT, {
        variables: { input: { projectId } },
        fetchPolicy: 'network-only',
        skip: skip || !projectId,
    })

    // Manual fetch for completed report after user (re)-runs analysis
    const [fetchReport, { loading: fetchedReportLoading, data: fetchedReportData }] = useLazyQuery<
        GetSpecIntelligenceReportResponse,
        GetSpecIntelligenceReportInput
    >(GET_SPEC_INTELLIGENCE_REPORT, { fetchPolicy: 'network-only' })

    const applyReport = useCallback((report: SpecIntelligenceReport) => {
        if (report.status === 'Complete') {
            setCurrentCompleteReport(report)
            setDrawerView('results')
        } else if (report.status === 'Failed') {
            setCurrentCompleteReport(report)
            setDrawerView('error')
        }
    }, [])

    // Reset to loading whenever queries are suppressed
    useEffect(() => {
        if (skip || !projectId) setDrawerView('loading')
    }, [skip, projectId])

    // React to initial report load
    useEffect(() => {
        if (skip || !projectId || initialReportLoading) return

        const report = initialReportData?.getLatestSpecIntelligenceReport
        if (!report) {
            // Query completed but no prior report — let the user run a fresh analysis
            if (initialReportData) setDrawerView('picker')
            return
        }
        if (report.status === 'Running' && report.instanceId) {
            startPolling(report.instanceId)
            setDrawerView('progress')
        } else {
            applyReport(report)
        }
    }, [initialReportLoading, initialReportData, skip, projectId, startPolling, applyReport])

    // React to report data from manual fetch after polling completes
    useEffect(() => {
        if (skip || !projectId || fetchedReportLoading) return
        const report = fetchedReportData?.getSpecIntelligenceReport
        if (report) applyReport(report)
    }, [fetchedReportLoading, fetchedReportData, skip, projectId, applyReport])

    // React to polling reaching Complete or Error
    useEffect(() => {
        if (!runStatus) return
        const reportId = runStatus.result?.reportId
        const needFetchReport = runStatus.state === 'Complete' && reportId
        if (needFetchReport) {
            fetchReport({
                variables: { input: { reportId } },
            })
        } else if (runStatus.state === 'Error') {
            setCurrentCompleteReport(null)
            setDrawerView('error')
        }
    }, [runStatus, fetchReport])

    // Start run mutation
    const [startRunMutation] = useMutation<
        StartSpecIntelligenceRunResponse,
        StartSpecIntelligenceRunInput
    >(START_SPEC_INTELLIGENCE_RUN)

    const startRun = useCallback(
        async (selectedLeafIds: string[]) => {
            try {
                const result = await startRunMutation({
                    variables: { input: { projectId, projectProductFamilyIds: selectedLeafIds } },
                })
                const instanceId = result.data?.startSpecIntelligenceRun?.instanceId
                if (instanceId) {
                    startPolling(instanceId)
                    setDrawerView('progress')
                }
            } catch {
                setDrawerView('error')
            }
        },
        [startRunMutation, projectId, startPolling]
    )

    const handleRerun = useCallback(() => {
        setRerunSectionNumbers(currentCompleteReport?.sectionNumbers ?? null)
        setCurrentCompleteReport(null)
        setDrawerView('picker')
    }, [currentCompleteReport])

    return { drawerView, isBuilding, runStatus, currentReport: currentCompleteReport, rerunSectionNumbers, startRun, handleRerun }
}

export default useSpecIntelligence
